import { useCallback, useMemo, useSyncExternalStore } from 'react';
import {
  basicsArticleOrder,
  basicsTotalCount,
  getSnapshot,
  getServerSnapshot,
  learningTracks,
  markArticleRead,
  resetProgress,
  resetTrack,
  subscribe,
} from '@/utils/learningProgress';

export function useReadArticleSlugs() {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}

export type TrackProgress = {
  slug: string;
  name: string;
  icon: string;
  done: number;
  total: number;
  isComplete: boolean;
};

/** Progress of every learning section (Forex พื้นฐาน, วิธีเทรด, MT4/MT5, ...). */
export function useTrackProgress(): TrackProgress[] {
  const readSlugs = useReadArticleSlugs();

  return useMemo(() => {
    const readSet = new Set(readSlugs);
    return learningTracks.map((track) => {
      const done = track.slugs.filter((slug) => readSet.has(slug)).length;
      const total = track.slugs.length;
      return {
        slug: track.slug,
        name: track.name,
        icon: track.icon,
        done,
        total,
        isComplete: total > 0 && done >= total,
      };
    });
  }, [readSlugs]);
}

export function useResetTrack() {
  return useCallback((trackSlug: string) => {
    resetTrack(trackSlug);
  }, []);
}

export function useLearningProgress() {
  const readSlugs = useReadArticleSlugs();

  const readSet = useMemo(() => new Set(readSlugs), [readSlugs]);

  const orderedReadCount = useMemo(
    () => basicsArticleOrder.filter((entry) => readSet.has(entry.slug)).length,
    [readSet],
  );

  const nextArticle = useMemo(
    () => basicsArticleOrder.find((entry) => !readSet.has(entry.slug)) ?? null,
    [readSet],
  );

  const lastReadArticle = useMemo(() => {
    for (let i = basicsArticleOrder.length - 1; i >= 0; i -= 1) {
      if (readSet.has(basicsArticleOrder[i].slug)) return basicsArticleOrder[i];
    }
    return null;
  }, [readSet]);

  const categoryProgress = useMemo(
    () =>
      Array.from(new Set(basicsArticleOrder.map((entry) => entry.categoryId))).map((categoryId) => {
        const entries = basicsArticleOrder.filter((entry) => entry.categoryId === categoryId);
        const done = entries.filter((entry) => readSet.has(entry.slug)).length;
        return {
          categoryId,
          categoryTitle: entries[0]?.categoryTitle ?? '',
          done,
          total: entries.length,
        };
      }),
    [readSet],
  );

  const percent = basicsTotalCount > 0 ? Math.round((orderedReadCount / basicsTotalCount) * 100) : 0;

  const toggleMarkRead = useCallback((slug: string) => {
    markArticleRead(slug);
  }, []);

  const clearProgress = useCallback(() => {
    resetProgress();
  }, []);

  return {
    readSlugs,
    readSet,
    readCount: orderedReadCount,
    total: basicsTotalCount,
    percent,
    nextArticle,
    lastReadArticle,
    categoryProgress,
    isComplete: basicsTotalCount > 0 && orderedReadCount >= basicsTotalCount,
    markRead: markArticleRead,
    toggleMarkRead,
    clearProgress,
  };
}